// Port of HistoryCompactor (SuiteModel/Compaction.swift). The web app never compacts (PROTOCOL.md §5); this exists so
// tests and the CLI (`wsc compact`, standing in for the Mac) can make compacted documents exactly as the Mac does: a new
// document replaying a sequence of the old one's states, stamped `epoch + 1` with a new lineage and a `CompactionRecord`,
// then `finish` (remapping anchor heads) and a last "Compacted history" commit.
//
// Kept states: every change of the last `recentDays`; the last state of each hour back to `hourlyDays`; the last state
// of each day before that; every pinned heads (checkpoints, anchors); every named change; and the current state last.
// Approximation: the Swift buckets days by the device's calendar, this by UTC days.
import * as A from '@automerge/automerge';
import { decodeHeads, encodeHeads } from '../automerge/heads.ts';
import { changesBeyond, headsList, type ChangeInfo } from './changes.ts';
import { epochIDOf, EPOCH_KEY, LINEAGE_KEY, writeCompactionRecord } from './epochID.ts';
import { readTree, sameTree, treeDifference, writeTree, type Backend, type DocTree, type RootTree } from './tree.ts';

export interface CompactionPolicy {
  recentDays: number;
  hourlyDays: number;
  minimumChanges: number;
  onlyIfWorthwhile: boolean;
}

export const STANDARD_POLICY: CompactionPolicy = { recentDays: 90, hourlyDays: 365, minimumChanges: 200, onlyIfWorthwhile: true };

/** `CompactionPolicy.worthwhile`: at least a tenth smaller, or half the changes. */
export function worthwhile(policy: CompactionPolicy, bytes: { before: number; after: number }, changes: { before: number; after: number }): boolean {
  return !policy.onlyIfWorthwhile || bytes.after <= bytes.before * 0.9 || changes.after * 2 <= changes.before;
}

/** Routine commit messages: states worth keeping only as the time buckets decide. */
const ROUTINE = new Set(['', 'Edited', 'Edit', 'Typing', 'Autosave', 'Saved']);
/** How many recent kept states the record maps, beside the pinned ones and the last. */
const RECORDED_STATES = 256;

export class CompactionFailure extends Error {}

export interface CompactionResult {
  doc: A.Doc<unknown>;
  /** Old heads (encoded) → the new document's heads for the same state. */
  headsMap: Record<string, string>;
  changesBefore: number;
  changesAfter: number;
}

export interface CompactOptions {
  pinned?: Iterable<string>;
  policy?: CompactionPolicy;
  now?: Date;
  lineage?: string;
  /** The new document's actor (random by default; fixtures pin it). */
  actor?: string;
  /** Edits the final state (remapped heads) before the last commit. */
  finish?: (raw: Backend, map: Record<string, string>) => void;
}

const seconds = (d: Date) => Math.floor(d.getTime() / 1000);

export function compactHistory(old: A.Doc<unknown>, o: CompactOptions = {}): CompactionResult {
  const policy = o.policy ?? STANDARD_POLICY;
  const now = o.now ?? new Date();
  const info = changesBeyond(old, []);
  const points: string[][] = [];
  for (const hex of o.pinned ?? []) {
    const heads = decodeHeads(hex);
    if (!heads.every((h) => info.has(h))) throw new CompactionFailure(`Compaction needs history this device doesn't have yet (heads ${hex.slice(0, 12)}…); sync, then try again.`);
    points.push(heads);
  }
  for (const c of info.values()) if (!ROUTINE.has(c.message ?? '')) points.push([c.hash]);
  const recent = seconds(now) - policy.recentDays * 86_400, hourly = seconds(now) - policy.hourlyDays * 86_400;
  const buckets = new Map<string, number>();
  for (const c of info.values()) {
    const t = c.time;
    const key = t >= recent ? `c${t}` : t >= hourly ? `h${Math.floor(t / 3600)}` : `d${Math.floor(t / 86_400)}`;
    buckets.set(key, Math.max(buckets.get(key) ?? -Infinity, t));
  }
  points.push(...timeCuts([...new Set(buckets.values())].sort((a, b) => a - b), info));

  const current = headsList(A.getHeads(old));
  const seen = new Set([encodeHeads(current)]);
  const ordered = points
    .map(headsList)
    .filter((h) => {
      const k = encodeHeads(h);
      if (!h.length || seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .map((h) => ({ heads: h, last: latest(h, info) }))
    .sort((a, b) => a.last.time - b.last.time);
  ordered.push({ heads: current, last: latest(current, info) });

  const raw = A.getBackend(A.init(o.actor ? { actor: o.actor } : undefined));
  const map: Record<string, string> = {};
  for (const { heads, last } of ordered) {
    const state = readTree(old, heads);
    writeTree(state, raw);
    raw.commit(last.message, last.time);
    map[encodeHeads(heads)] = encodeHeads(raw.getHeads());
    const written = readTree(raw);
    if (!sameTree(written, state)) throw new CompactionFailure(`The compacted history didn't match the original (the state at ${last.time}, ${treeDifference(written, state)}), so the original was kept.`);
  }
  const currentState = readTree(raw);
  const from = epochIDOf(old), fromHeads = encodeHeads(current);
  const pinned = new Set(o.pinned ?? []);
  const recorded = new Set([...pinned, ...ordered.slice(-RECORDED_STATES).map((s) => encodeHeads(s.heads))]);
  raw.put('_root', EPOCH_KEY, from.epoch + 1, 'int');
  raw.put('_root', LINEAGE_KEY, o.lineage ?? crypto.randomUUID().toLowerCase(), 'str');
  writeCompactionRecord(raw, {
    from, fromHeads, toHeads: map[fromHeads] ?? '',
    headsMap: Object.fromEntries(Object.entries(map).filter(([k]) => recorded.has(k))), absorbedHeads: '',
  });
  o.finish?.(raw, map);
  raw.commit('Compacted history', seconds(now));
  for (const [oldHex, newHex] of Object.entries(map)) {
    if (!pinned.has(oldHex)) continue;
    if (!sameTree(readTree(old, decodeHeads(oldHex)), readTree(raw, decodeHeads(newHex)))) throw new CompactionFailure('The compacted history didn’t match the original (a checkpoint or anchor), so the original was kept.');
  }
  const original = readTree(old);
  if (!sameTree(currentState, original)) throw new CompactionFailure(`The compacted history didn't match the original (the current state, ${treeDifference(currentState, original)}), so the original was kept.`);
  const doc = A.load<unknown>(raw.save());
  return { doc, headsMap: map, changesBefore: info.size, changesAfter: A.getAllChanges(doc).length };
}

/** The frontier of every change made at or before each of `cuts` (ascending), and whatever those depend on. */
function timeCuts(cuts: number[], info: Map<string, ChangeInfo>): string[][] {
  const byTime = [...info.values()].sort((a, b) => a.time - b.time);
  const included = new Set<string>();
  const front = new Set<string>();
  const include = (hash: string) => {
    const stack = [hash];
    while (stack.length) {
      const top = stack[stack.length - 1];
      if (included.has(top)) {
        stack.pop();
        continue;
      }
      const deps = info.get(top)?.deps ?? [];
      const missing = deps.filter((d) => !included.has(d) && info.has(d));
      if (missing.length) {
        stack.push(...missing);
        continue;
      }
      stack.pop();
      included.add(top);
      for (const d of deps) front.delete(d);
      front.add(top);
    }
  };
  let next = 0;
  const out: string[][] = [];
  for (const cut of cuts) {
    while (next < byTime.length && byTime[next].time <= cut) include(byTime[next++].hash);
    out.push([...front]);
  }
  return out;
}

/** The latest change among `heads`' ancestry (for its time and message). */
function latest(heads: string[], info: Map<string, ChangeInfo>): { time: number; message: string | null } {
  let newest: ChangeInfo | undefined;
  for (const h of heads) {
    const c = info.get(h);
    if (c && (!newest || c.time > newest.time)) newest = c;
  }
  return { time: newest?.time ?? 0, message: newest?.message ?? null };
}

/** Every string under a key named `key`, anywhere in the tree (anchor heads). */
export function stringsForKey(key: string, tree: RootTree): Set<string> {
  const found = new Set<string>();
  const walk = (t: DocTree, k: string | null) => {
    if (t.kind === 'scalar' && t.type === 'str' && k === key) found.add(t.value as string);
    else if (t.kind === 'map') for (const [ck, c] of t.entries) walk(c, ck);
    else if (t.kind === 'list') for (const c of t.items) walk(c, null);
  };
  for (const [k, v] of tree) walk(v, k);
  return found;
}

/** Replaces every string under `key` found in `map` (anchor heads), in place. */
export function remapStrings(key: string, raw: Backend, map: Record<string, string>): void {
  const walk = (obj: string, type: 'map' | 'list') => {
    if (type === 'map') {
      for (const k of raw.keys(obj)) {
        const v = raw.getWithType(obj, k) as [string, unknown] | null;
        if (!v) continue;
        if (v[0] === 'str' && k === key && map[v[1] as string]) raw.put(obj, k, map[v[1] as string], 'str');
        else if (v[0] === 'map' || v[0] === 'list') walk(v[1] as string, v[0]);
      }
    } else {
      for (let i = 0; i < raw.length(obj); i++) {
        const v = raw.getWithType(obj, i) as [string, unknown] | null;
        if (v && (v[0] === 'map' || v[0] === 'list')) walk(v[1] as string, v[0]);
      }
    }
  };
  walk('_root', 'map');
}

/**
 * A fragment compacted as `DocumentStore.compactFragment` does it: pinned = every comment and suggestion anchor plus
 * the given checkpoint heads; the anchors remapped in the final state.
 */
export function compactFragmentDoc(doc: A.Doc<unknown>, o: Omit<CompactOptions, 'finish' | 'pinned'> & { checkpointHeads?: Iterable<string> } = {}): CompactionResult {
  const pinned = new Set([...stringsForKey('anchorHeads', readTree(doc)), ...(o.checkpointHeads ?? [])]);
  return compactHistory(doc, { ...o, pinned, finish: (raw, map) => remapStrings('anchorHeads', raw, map) });
}

/** As `DocumentStore.compactProjectDocument`: pinned = the checkpoints' project heads, remapped in the final state. */
export function compactProjectDoc(doc: A.Doc<unknown>, o: Omit<CompactOptions, 'finish' | 'pinned'> = {}): CompactionResult {
  const pinned = new Set<string>();
  const cps = readTree(doc).get('checkpoints');
  if (cps?.kind === 'map') {
    for (const cp of cps.entries.values()) {
      const h = cp.kind === 'map' ? cp.entries.get('projectHeads') : undefined;
      if (h?.kind === 'scalar' && h.type === 'str' && h.value) pinned.add(h.value as string);
    }
  }
  return compactHistory(doc, { ...o, pinned, finish: (raw, map) => remapStrings('projectHeads', raw, map) });
}
